import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { professionalProfiles, professionalCategories, serviceCategories } from '@afficheservices/db/schema'
import { sql, eq, ilike, desc, and } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { formatRating } from '~/lib/format'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import {
  BadgeCheck, ArrowRight, CheckCircle, Shield, MapPin,
  Star, Search, Sparkles, ExternalLink,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Artisans RGE près de chez vous — Annuaire certifié 2026 | AfficheServices' },
  { name: 'description', content: 'Trouvez un artisan RGE (Reconnu Garant de l\'Environnement) pour vos travaux de rénovation énergétique. Obligatoire pour MaPrimeRénov\' et CEE. Annuaire vérifié.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/rge' },
]

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const cityFilter = url.searchParams.get('ville') ?? ''

  // Count RGE-eligible pros (those in energy-related categories)
  const _energySlugs = ['climatisation', 'isolation', 'plomberie', 'electricite', 'toiture', 'couverture-zinguerie', 'renovation']

  const conditions = [
    sql`${serviceCategories.slug} IN ('climatisation','isolation','plomberie','electricite','toiture','couverture-zinguerie','renovation')`,
  ]
  if (cityFilter) {
    conditions.push(ilike(professionalProfiles.city, `%${cityFilter}%`))
  }

  const pros = await db.select({
    id: professionalProfiles.id,
    companyName: professionalProfiles.companyName,
    slug: professionalProfiles.slug,
    city: professionalProfiles.city,
    departmentCode: professionalProfiles.departmentCode,
    ratingAvg: professionalProfiles.ratingAvg,
    reviewCount: professionalProfiles.reviewCount,
    siretVerified: professionalProfiles.siretVerified,
    categoryName: serviceCategories.nameFr,
  })
    .from(professionalProfiles)
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .innerJoin(serviceCategories, and(
      eq(serviceCategories.id, professionalCategories.categoryId),
      sql`${serviceCategories.slug} IN ('climatisation','isolation','plomberie','electricite','toiture','couverture-zinguerie','renovation')`,
    ))
    .where(cityFilter ? ilike(professionalProfiles.city, `%${cityFilter}%`) : sql`1=1`)
    .orderBy(desc(professionalProfiles.siretVerified))
    .limit(50)

  // Dedupe by pro id (a pro can be in multiple energy categories)
  const seen = new Set<string>()
  const uniquePros = pros.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true })

  const [totalRow] = await db.select({ count: sql<number>`count(DISTINCT ${professionalProfiles.id})::int` })
    .from(professionalProfiles)
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .innerJoin(serviceCategories, and(
      eq(serviceCategories.id, professionalCategories.categoryId),
      sql`${serviceCategories.slug} IN ('climatisation','isolation','plomberie','electricite','toiture','couverture-zinguerie','renovation')`,
    ))

  return { pros: uniquePros, total: totalRow?.count ?? 0, cityFilter }
}

export default function RGEPage() {
  const { pros, total, cityFilter } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Rénovation énergétique', url: 'https://www.afficheservices.fr/renovation-energetique' },
        { name: 'Artisans RGE', url: 'https://www.afficheservices.fr/rge' },
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-violet-200">
            <BadgeCheck className="h-3.5 w-3.5" /> {total.toLocaleString('fr-FR')} artisans énergie référencés
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl">Artisans RGE certifiés</h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-violet-200">
            Un artisan RGE est obligatoire pour bénéficier de MaPrimeRénov', des CEE et de l'éco-PTZ.
            Trouvez un professionnel certifié près de chez vous.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-10 lg:px-6">
        {/* Search by city */}
        <form method="get" className="mx-auto max-w-xl">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                name="ville"
                defaultValue={cityFilter}
                placeholder="Filtrer par ville (ex: Nice, Paris, Lyon...)"
                className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <button type="submit" className="flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
              <Search className="h-4 w-4" /> Rechercher
            </button>
          </div>
        </form>

        {cityFilter && (
          <p className="mt-4 text-center text-sm text-slate-500">
            {pros.length} artisan{pros.length > 1 ? 's' : ''} énergie à « {cityFilter} »
            {' · '}
            <Link to="/rge" className="text-primary hover:underline">Voir tous</Link>
          </p>
        )}

        {/* Qu'est-ce que RGE */}
        <section className="mt-10 rounded-2xl border border-violet-100 bg-violet-50 p-6">
          <h2 className="text-lg font-bold text-violet-900">Qu'est-ce que le label RGE ?</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 text-sm text-violet-800">
            <div>
              <p><strong>RGE = Reconnu Garant de l'Environnement.</strong> C'est un label délivré par des organismes accrédités (Qualibat, QualiPAC, Qualit'EnR, Qualifelec) attestant que l'artisan maîtrise les techniques de rénovation énergétique.</p>
            </div>
            <div>
              <p><strong>Obligatoire pour les aides :</strong> MaPrimeRénov', CEE, éco-PTZ. Sans artisan RGE, vous perdez toutes les subventions — soit souvent 30-50 % du budget total.</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-violet-600">
            Vérifiez toujours le label sur{' '}
            <a href="https://france-renov.gouv.fr/annuaire-rge" target="_blank" rel="noopener noreferrer" className="font-semibold underline">
              france-renov.gouv.fr <ExternalLink className="inline h-3 w-3" />
            </a>
          </p>
        </section>

        {/* Pro cards */}
        <section className="mt-10">
          <h2 className="text-lg font-bold text-slate-900">
            {cityFilter ? `Artisans énergie à ${cityFilter}` : 'Artisans énergie en France'}
          </h2>
          <div className="mt-4 space-y-3">
            {pros.length === 0 ? (
              <div className="rounded-xl border border-border bg-white p-8 text-center shadow-xs">
                <p className="text-sm text-slate-500">Aucun artisan trouvé{cityFilter ? ` à « ${cityFilter} »` : ''}.</p>
                <Link to="/rge" className="mt-2 inline-flex text-sm font-semibold text-primary hover:underline">Voir tous les artisans énergie</Link>
              </div>
            ) : (
              pros.map((pro) => (
                <Link
                  key={pro.id}
                  to={`/pro/${pro.slug}`}
                  className="group flex items-center gap-4 rounded-xl border border-border bg-white p-4 shadow-xs transition-all hover:shadow-md hover:border-primary/30"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary">
                    {pro.companyName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900">{pro.companyName}</span>
                      {pro.siretVerified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-primary" />}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      {(pro.reviewCount ?? 0) > 0 ? (
                        <span className="flex items-center gap-0.5">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          {formatRating(pro.ratingAvg)}
                        </span>
                      ) : (
                        <span className="flex items-center gap-0.5 text-blue-500">
                          <Sparkles className="h-3 w-3" /> Nouveau
                        </span>
                      )}
                      <span>·</span>
                      <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" /> {pro.city} ({pro.departmentCode})</span>
                      <span>·</span>
                      <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-600">{pro.categoryName}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-primary" />
                </Link>
              ))
            )}
          </div>
          {pros.length >= 50 && (
            <p className="mt-4 text-center text-xs text-slate-400">Affichage limité à 50 résultats. Utilisez le filtre ville pour affiner.</p>
          )}
        </section>

        {/* Trust signals */}
        <section className="mt-12 grid gap-4 sm:grid-cols-3">
          {[
            { icon: CheckCircle, color: 'text-emerald-500', title: 'SIRET vérifié', desc: 'Chaque artisan est référencé via le registre SIRENE officiel (INSEE).' },
            { icon: Shield, color: 'text-blue-500', title: 'Certification RGE', desc: 'Le label RGE garantit la compétence en rénovation énergétique.' },
            { icon: BadgeCheck, color: 'text-violet-500', title: 'Éligible aux aides', desc: 'Travaux réalisés par un RGE = MaPrimeRénov\', CEE, éco-PTZ.' },
          ].map((t) => (
            <div key={t.title} className="flex gap-3 rounded-xl border border-border bg-white p-4 shadow-xs">
              <t.icon className={`mt-0.5 h-5 w-5 shrink-0 ${t.color}`} />
              <div>
                <p className="text-sm font-semibold text-slate-900">{t.title}</p>
                <p className="mt-0.5 text-xs text-slate-500">{t.desc}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      <Footer />
    </div>
  )
}
